/**
 * Vue I18n is an internationalization plugin for Vue.js. It easily integrates localization features.
 *
 * This file creates a vue-i18n instance with the following configuration:
 *    const i18n = createI18n({
 *      locale: 'en', // set locale
 *      messages, // set locale messages
 *    })
 *
 * Usage:
 *
 * In Vue components, you can now use the t method to translate your application.
 * Template example:
 *    <p>{{ t('foo.bar') }}</p>
 *
 *    <MyComponent :label="t('foo.bar')" />
 *
 * Script example using Composition API:
 *    import { useI18n } from 'vue-i18n'
 *    const { t } = useI18n()
 *    const label = t('foo.bar')
 *
 * You can change the locale using the locale.value property:
 *    const { locale } = useI18n()
 *    locale.value = 'fr'
 *
 * The translation messages are located in the src/i18n//locales directory, organized by language and namespace.
 *
 * Environment-Specific Behavior:
 * - In development mode: Uses local JSON files from src/i18n/locales
 * - In production mode: Fetches translations from Locize CDN: https://api.locize.app/{LOCIZE_PROJECT_ID}/latest/{lang}/{namespace}
 *   where multiple namespaces are fetched based on the local JSON structure
 *
 * Translation Overrides:
 * - Special namespaces (like 'bottimmo') are used to override base translations
 * - Overrides are applied after fetching all namespaces
 *
 * For more information, check out the official Vue I18n documentation: https://vue-i18n.intlify.dev/
 */
import * as Sentry from '@sentry/vue'
import { createI18n } from 'vue-i18n'

import { OVERRIDE_NAMESPACES } from './helpers/constants'
import { getClientLocale, getFallbackLocale, saveLocaleToStorage } from './helpers/locale'
import { createLocizeFetcher } from './helpers/locize'
import { createLoadOverrides } from './helpers/overrides'
import {
  getActiveOverrideNamespaces,
  getCurrentRoutePath,
  setCurrentRoutePath,
} from './helpers/route'
import { createSentryReporter } from './helpers/sentry'
import { buildTranslationCatalog, getFallbackLocalTranslations } from './helpers/translations'

const isDevMode = !import.meta.env.PROD
const LOCIZE_PROJECT_ID = import.meta.env.VITE_LOCIZE_PROJECT_ID

if (!isDevMode && !LOCIZE_PROJECT_ID) {
  Sentry.captureMessage('Missing VITE_LOCIZE_PROJECT_ID environment variable', {
    level: 'error',
    tags: { component: 'i18n' },
  })
}

const { allNamespaces, availableLocales, mergedMessages } = buildTranslationCatalog()

const resolveFallbackLocale = (locale) => getFallbackLocale(locale, availableLocales)

const sentryReporter = createSentryReporter({
  availableLocales,
  allNamespaces,
  overrideNamespaces: OVERRIDE_NAMESPACES,
  locizeProjectId: LOCIZE_PROJECT_ID,
  getFallbackLocale: resolveFallbackLocale,
  getActiveOverrideNamespaces,
  getCurrentRoutePath,
})

const { reportI18nIssue } = sentryReporter

const { fetchLocizeNamespace, fetchLocizeTranslations, lastFetchDiagnostics } = createLocizeFetcher(
  {
    isDevMode,
    locizeProjectId: LOCIZE_PROJECT_ID,
    allNamespaces,
    sentryReporter,
  }
)

const loadingLocales = new Map()
const localeTranslationsCache = new Map()
const fallbackLoadTriggeredFor = new Set()

/** @type {(locale: string) => Promise<void>} */
let lazyLoadFallbackLocales = async () => {}

const clientLocale = getClientLocale(availableLocales)

const i18nInstance = createI18n({
  locale: clientLocale,
  fallbackLocale: resolveFallbackLocale(clientLocale),
  messages: isDevMode ? mergedMessages : {},
  legacy: false,
  missingWarn: false,
  fallbackWarn: false,
  missing: (locale) => {
    if (!isDevMode) {
      void lazyLoadFallbackLocales(locale)
    }
  },
})

const loadOverrides = createLoadOverrides({
  i18nInstance,
  isDevMode,
  allNamespaces,
  fetchLocizeNamespace,
  getActiveOverrideNamespaces,
  getCurrentRoutePath,
  cacheLocaleTranslations: (locale, translations) => {
    localeTranslationsCache.set(locale, translations)
  },
})

async function loadLocaleTranslations(locale, action) {
  if (!isDevMode) {
    const translations = await fetchLocizeTranslations(locale)

    if (translations && Object.keys(translations).length > 0) {
      localeTranslationsCache.set(locale, translations)
      await loadOverrides(locale, translations, getCurrentRoutePath())
      return
    }

    const localFallback = getFallbackLocalTranslations(locale)
    reportI18nIssue('error', `No translations available for ${locale}`, {
      lang: locale,
      action,
      namespaceResults: lastFetchDiagnostics.get(locale) || null,
      localFallbackNamespaceCount: Object.keys(localFallback).length,
      loadedNamespaces: Object.keys(translations || {}),
    })
    return
  }

  await loadOverrides(locale, null, getCurrentRoutePath())
}

async function ensureFallbackLocalesLoaded(locale) {
  const fallbackLocales = resolveFallbackLocale(locale)
  const fallbackArray = Array.isArray(fallbackLocales) ? fallbackLocales : [fallbackLocales]
  let success = true

  for (const fallbackLocale of fallbackArray) {
    if (fallbackLocale === locale) continue
    if (i18nInstance.global.messages.value[fallbackLocale]) continue

    if (loadingLocales.has(fallbackLocale)) {
      try {
        await loadingLocales.get(fallbackLocale)
      } catch {
        success = false
      }
    } else {
      const loadPromise = loadLocaleTranslations(
        fallbackLocale,
        'ensureFallbackLocalesLoaded'
      ).catch((error) => {
        loadingLocales.delete(fallbackLocale)
        Sentry.captureException(error, {
          tags: { component: 'i18n' },
          extra: { locale: fallbackLocale, action: 'ensureFallbackLocalesLoaded' },
        })
        throw error
      })
      loadingLocales.set(fallbackLocale, loadPromise)

      try {
        await loadPromise
      } catch {
        success = false
      }
    }

    if (!i18nInstance.global.messages.value[fallbackLocale]) {
      success = false
    }
  }

  return success
}

lazyLoadFallbackLocales = async function lazyLoadFallbackLocalesImpl(locale) {
  if (fallbackLoadTriggeredFor.has(locale)) return
  fallbackLoadTriggeredFor.add(locale)

  const success = await ensureFallbackLocalesLoaded(locale)
  if (!success) {
    fallbackLoadTriggeredFor.delete(locale)
  }
}

async function loadCurrentLocaleTranslations() {
  const currentLocale = i18nInstance.global.locale.value

  // If the current locale is a variant that does not exist as a standalone locale
  // in translation sources (e.g. de-CH), rely on fallback loading instead of
  // attempting a direct fetch for the variant locale.
  if (!isDevMode && !availableLocales.includes(currentLocale)) {
    await ensureFallbackLocalesLoaded(currentLocale)
    return
  }

  if (loadingLocales.has(currentLocale)) {
    return loadingLocales.get(currentLocale)
  }

  const loadPromise = (async () => {
    try {
      await loadLocaleTranslations(currentLocale, 'loadCurrentLocaleTranslations')
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'i18n' },
        extra: { locale: currentLocale, action: 'loadCurrentLocaleTranslations' },
      })
      loadingLocales.delete(currentLocale)
      throw error
    }
  })()

  loadingLocales.set(currentLocale, loadPromise)
  return loadPromise
}

loadCurrentLocaleTranslations()

async function syncRouteOverrides(path) {
  setCurrentRoutePath(path)

  const currentLocale = i18nInstance.global.locale.value

  if (loadingLocales.has(currentLocale)) {
    try {
      await loadingLocales.get(currentLocale)
    } catch {
      // initial load failed; fall through and let loadOverrides retry
    }
  }

  const cachedTranslations = localeTranslationsCache.get(currentLocale) || null
  await loadOverrides(currentLocale, cachedTranslations, path)
}

const i18n = {
  availableLocales,

  /**
   * Returns the i18n instance.
   * @returns {Object} i18n instance
   */
  getInstance() {
    return i18nInstance
  },

  async syncRouteOverrides(path) {
    await syncRouteOverrides(path)
  },

  async changeLocale(locale) {
    saveLocaleToStorage(locale)
    i18nInstance.global.fallbackLocale.value = resolveFallbackLocale(locale)

    // If the locale variant doesn't exist in our translations (e.g. 'de-DE' when only 'de' exists),
    // skip fetching from locize — translations don't exist there either. Just ensure the base
    // fallback locale is loaded, then set the locale so vue-i18n's fallback chain handles it.
    if (!isDevMode && !availableLocales.includes(locale)) {
      await ensureFallbackLocalesLoaded(locale)
      i18nInstance.global.locale.value = locale
      return
    }

    // If this locale is already being loaded, wait for it to complete
    if (loadingLocales.has(locale)) {
      await loadingLocales.get(locale)
    } else {
      const loadPromise = (async () => {
        try {
          await loadLocaleTranslations(locale, 'changeLocale')
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'i18n' },
            extra: { locale, action: 'changeLocale' },
          })
          loadingLocales.delete(locale)
          throw error
        }
      })()

      loadingLocales.set(locale, loadPromise)
      await loadPromise
    }

    i18nInstance.global.locale.value = locale
  },
}

export default i18n
